import { create } from 'zustand';
import { BatchedMessage, socketManager, SocketMessage } from '../../../utils/socket-manager';
import { useAuthStore } from '../auth/useAuthStore';
import { ConfigurationSettings } from 'models/configuration-settings';

export interface StreamingQueueItem {
  position: number;
  conversationId: string;
  messageID: string;
  type: string;
  userID: string[] | null;
  parentIds: string[];
  streamingResponse: any[];
}

interface CollaborationSocketState {
  connectionStatus: 'DISCONNECTED' | 'CONNECTED' | 'RECONNECTING';
  otherMessage: any | null;
  streamingMessage: any | null;
  // Batched streaming messages for bulk processing
  streamingMessageBatch: any[];
  streamingQueue: StreamingQueueItem[];
  unsubscribeFn: (() => void) | null;
  configurationSettings: ConfigurationSettings | null;
  init: (configurationSettings: ConfigurationSettings) => void;
  sendMessage: (payload: any) => void;
  clearMessages: () => void;
  clearStreamingQueue: () => void;
  removeFromStreamingQueue: (positions: number[]) => void;
}

const categorizeMessage = (msg: any): { isStreaming: boolean; isOther: boolean } => {
  const isStreaming = msg.type === 'ASSISTANT_STREAMED_RESPONSE' || msg.type === 'USER_MESSAGE';
  const isOther = !isStreaming;
  return { isStreaming, isOther };
};

export const useCollaborationSocketStore = create<CollaborationSocketState>((set, get) => ({
  connectionStatus: 'DISCONNECTED',
  otherMessage: null,
  streamingMessage: null,
  streamingMessageBatch: [],
  streamingQueue: [],
  unsubscribeFn: null,
  configurationSettings: null,

  init: (configurationSettings: ConfigurationSettings) => {
    // Unsubscribe from previous listener if it exists to prevent duplicates
    const currentUnsubscribe = get().unsubscribeFn;
    if (currentUnsubscribe) {
      currentUnsubscribe();
    }

    const accessToken = useAuthStore.getState().accessToken;
    const protocol = accessToken ? accessToken : '';

    const url = `wss://${configurationSettings.paths.baseUrl}${configurationSettings.paths.taskforce.collaboration}`;
    socketManager.connect(url, protocol);

    // Store configuration settings for later use
    set({ configurationSettings });

    // Subscribe and store the unsubscribe function
    const unsubscribe = socketManager.subscribe((data: SocketMessage) => {
      // Handle connection status events immediately
      if (data.type === '__STATUS__') {
        set({ connectionStatus: data.status });
        return;
      }

      // Handle batched messages from RAF batching
      if (data.type === '__BATCH__') {
        const batchData = data as BatchedMessage;
        const messages = batchData.messages;

        if (messages.length === 0) return;

        // Process all messages in batch and extract the last of each type
        let lastOtherMessage: any = null;
        let lastStreamingMessage: any = null;
        const streamingMessages: any[] = [];

        for (const msg of messages) {
          const { isStreaming, isOther } = categorizeMessage(msg);

          if (isOther) {
            lastOtherMessage = msg;
          }

          if (isStreaming) {
            lastStreamingMessage = msg;
            streamingMessages.push(msg);
          }
        }

        // Single set() call for the entire batch - this is the key optimization
        set({
          otherMessage: lastOtherMessage,
          streamingMessage: lastStreamingMessage,
          streamingMessageBatch: streamingMessages,
        });

        return;
      }

      // Fallback for non-batched messages (shouldn't happen with RAF batching,
      // but kept for backward compatibility during transition)
      const { isStreaming, isOther } = categorizeMessage(data);

      set({
        otherMessage: isOther ? data : null,
        streamingMessage: isStreaming ? data : null,
        streamingMessageBatch: isStreaming ? [data] : [],
      });
    });

    // Store the unsubscribe function
    set({ unsubscribeFn: unsubscribe });
  },

  sendMessage: (payload: any) => {
    socketManager.send(payload);
  },

  clearMessages: () =>
    set({
      otherMessage: null,
      streamingMessage: null,
      streamingMessageBatch: [],
    }),

  clearStreamingQueue: () =>
    set({
      streamingQueue: [],
    }),

  removeFromStreamingQueue: (positions: number[]) =>
    set((state) => {
      const currentQueue = [...state.streamingQueue];
      const filteredQueue = currentQueue.filter((item) => !positions.includes(item.position));
      return { streamingQueue: filteredQueue };
    }),
}));
